import React from 'react';
import { Box, Flex, SimpleGrid, Skeleton, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  Buildings,
  CaretDown,
  CaretUp,
  CheckCircle,
  CurrencyCircleDollar,
  ListChecks,
  Receipt,
} from 'phosphor-react';

import { GlassCard } from '../../../../../components/glass';
import type {
  IContaCilDashboardKpis,
  IContaCilMesStatus,
} from '../../../../../hooks/useContaCilDashboard';
import { InfoTip } from './InfoTip';

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

/** Mini-gráfico de tendência (a "batida de coração") — SVG puro, sem lib. */
export const Sparkline = ({ data, color }: { data: number[]; color: string }) => {
  const pts = data.length >= 2 ? data : [0, 0];
  const max = Math.max(...pts);
  const min = Math.min(...pts);
  const range = max - min || 1;
  const w = 72;
  const h = 26;
  const step = w / (pts.length - 1);
  const coords = pts.map(
    (v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`,
  );
  const line = coords.join(' ');
  const [lastX, lastY] = coords[coords.length - 1].split(',');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow: 'visible' }} aria-hidden>
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r={2.2} fill={color} />
    </svg>
  );
};

/** Cardiograma multi-linha (escala compartilhada) — linhas se sobrepõem/cruzam. */
export const MultiSparkline = ({ series }: { series: { data: number[]; color: string }[] }) => {
  const clean = series.map(s => ({
    ...s,
    data: s.data.map(v => (Number.isFinite(v) ? v : 0)),
  }));
  const all = clean.flatMap(s => s.data);
  const max = all.length ? Math.max(...all) : 0;
  const min = all.length ? Math.min(...all) : 0;
  const range = max - min || 1;
  const w = 78;
  const h = 26;
  const len = Math.max(2, ...clean.map(s => s.data.length));
  const step = w / (len - 1);
  const y = (v: number) => (h - ((v - min) / range) * h).toFixed(1);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow: 'visible' }} aria-hidden>
      {clean.map(s => {
        if (s.data.length < 2) return null;
        const pts = s.data.map((v, i) => `${(i * step).toFixed(1)},${y(v)}`);
        const [lx, ly] = pts[pts.length - 1].split(',');
        return (
          <g key={s.color}>
            <polyline
              points={pts.join(' ')}
              fill="none"
              stroke={s.color}
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={0.95}
            />
            <circle cx={lx} cy={ly} r={1.9} fill={s.color} />
          </g>
        );
      })}
    </svg>
  );
};

interface KPIDef {
  key: keyof IContaCilDashboardKpis;
  label: string;
  icon: React.ReactNode;
  accent: string;
  format?: (n: number) => string;
  // Série mensal pra sparkline (12 pontos, de meses_status).
  serie: (meses: IContaCilMesStatus[]) => number[];
  // true = cair é BOM (ex: pendentes) → queda fica verde.
  positiveWhenDown?: boolean;
  // Sub-linha (contagem EXPORTÁVEL — partida-dobrada) abaixo do valor.
  sub?: (d: IContaCilDashboardKpis) => string | null;
  // Tooltip explicativo.
  info: string;
}

const KPIS: KPIDef[] = [
  {
    key: 'empresas_total',
    label: 'Empresas',
    icon: <Buildings size={18} weight="duotone" />,
    accent: '#6366F1',
    serie: ms => ms.map(m => m.empresas),
    info: 'Total de empresas na base, dividido em Ativas (dt_cli_ate em aberto) e Inativas (com fim de cliente). A sparkline mostra as empresas com movimentação por mês.',
  },
  {
    key: 'lancamentos_total',
    label: 'Lançamentos',
    icon: <Receipt size={18} weight="duotone" />,
    accent: '#1B88F5',
    serie: ms => ms.map(m => m.total),
    sub: d => `${formatNumber(d.lancamentos_exportaveis)} exportáveis`,
    info: 'Total de lançamentos importados. "Exportáveis" = partida-dobrada completa (débito + crédito), pronta pro Domínio.',
  },
  {
    key: 'conciliados',
    label: 'Conciliados',
    icon: <CheckCircle size={18} weight="duotone" />,
    accent: '#10B981',
    serie: ms => ms.map(m => m.conciliados),
    sub: d =>
      d.lancamentos_exportaveis > 0
        ? `${d.percent_conciliado_exportavel.toFixed(1)}% exportável`
        : null,
    info: 'Lançamentos vinculados ao plano de contas. "% exportável" considera só os de partida-dobrada.',
  },
  {
    key: 'pendentes',
    label: 'Pendentes',
    icon: <ListChecks size={18} weight="duotone" />,
    accent: '#F59E0B',
    serie: ms => ms.map(m => m.pendentes),
    positiveWhenDown: true,
    sub: d => `${formatNumber(d.pendentes_exportaveis)} exportáveis`,
    info: 'Lançamentos ainda não conciliados. Cair é bom (fica verde na variação).',
  },
  {
    key: 'enviados_cliente',
    label: 'Recebidos do cliente',
    icon: <CurrencyCircleDollar size={18} weight="duotone" />,
    accent: '#8B5CF6',
    serie: ms => ms.map(m => m.enviados_cliente),
    info: 'Lançamentos cuja origem é o próprio cliente (não o escritório).',
  },
];

/** Variação do último mês com dado vs o mês anterior (month-over-month). */
export function computeDelta(serie: number[]): number | null {
  let last = -1;
  for (let i = serie.length - 1; i >= 0; i -= 1) {
    if (serie[i] > 0) {
      last = i;
      break;
    }
  }
  if (last <= 0) return null;
  return serie[last] - serie[last - 1];
}

/** Cor do badge de variação: verde na direção "boa", vermelho na ruim. */
export function deltaColorFor(delta: number | null, positiveWhenDown?: boolean): string {
  if (delta == null || delta === 0) return '#94A3B8';
  const good = positiveWhenDown ? delta < 0 : delta > 0;
  return good ? '#10B981' : '#EF4444';
}

interface KPICardsProps {
  data?: IContaCilDashboardKpis;
  meses?: IContaCilMesStatus[];
  isLoading?: boolean;
}

// Linha Ativas/Inativas do card Empresas: bolinha + rótulo + barra de proporção
// (a "linha" na cor de cada uma) + contagem na cor.
const EmpresaLinha = ({
  label,
  valor,
  total,
  cor,
}: {
  label: string;
  valor: number;
  total: number;
  cor: string;
}) => {
  const pct = total > 0 ? (valor / total) * 100 : 0;
  return (
    <Flex align="center" gap={2}>
      <Box w="7px" h="7px" borderRadius="full" bg={cor} flexShrink={0} />
      <Text fontSize="11px" color="#64748B" fontWeight="500" w="42px" flexShrink={0}>
        {label}
      </Text>
      <Box
        flex={1}
        h="4px"
        borderRadius="full"
        bg="rgba(15,23,42,0.06)"
        overflow="hidden"
        minW="16px"
      >
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          style={{ height: '100%', background: cor, borderRadius: '9999px' }}
        />
      </Box>
      <Text
        fontSize="13px"
        fontWeight="700"
        color={cor}
        w="38px"
        textAlign="right"
        flexShrink={0}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatNumber(valor)}
      </Text>
    </Flex>
  );
};

export const KPICards = ({ data, meses, isLoading }: KPICardsProps) => {
  return (
    <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4} mb={8}>
      {KPIS.map((kpi, idx) => {
        const value = data?.[kpi.key] ?? 0;
        const serie = meses ? kpi.serie(meses) : [];
        const delta = serie.length ? computeDelta(serie) : null;
        const deltaColor = deltaColorFor(delta, kpi.positiveWhenDown);
        return (
          <motion.div
            key={kpi.key}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.06 * idx, ease: [0.4, 0, 0.2, 1] }}
          >
            <GlassCard intensity="strong" p={0} h="full" overflow="hidden">
              {/* barra de acento no topo */}
              <Box h="3px" w="full" bg={kpi.accent} />
              <Flex direction="column" gap={2} h="full" p={5}>
                <Flex align="center" justify="space-between">
                  <Flex align="center" gap={1.5}>
                    <Box color={kpi.accent} display="flex">
                      {kpi.icon}
                    </Box>
                    <Text
                      fontSize="10px"
                      fontWeight="600"
                      color="#94A3B8"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                    >
                      {kpi.label}
                    </Text>
                  </Flex>
                  <InfoTip label={kpi.info} size={12} />
                </Flex>

                <Flex align="flex-end" justify="space-between" gap={2}>
                  {isLoading ? (
                    <Skeleton height="30px" width="60%" mt={1} borderRadius="md" />
                  ) : (
                    <Text
                      fontSize={{ base: '24px', md: '28px' }}
                      fontWeight="700"
                      color="#0F172A"
                      letterSpacing="-0.02em"
                      lineHeight="1.1"
                    >
                      {kpi.format ? kpi.format(value) : formatNumber(value)}
                    </Text>
                  )}
                  {!isLoading && kpi.key === 'empresas_total' && meses && meses.length > 1 && (
                    <Box flexShrink={0} pb={1}>
                      <MultiSparkline
                        series={[
                          { data: meses.map(m => m.empresas ?? 0), color: '#6366F1' },
                          { data: meses.map(m => m.empresas_ativas ?? 0), color: '#0EA371' },
                          { data: meses.map(m => m.empresas_inativas ?? 0), color: '#E0524D' },
                        ]}
                      />
                    </Box>
                  )}
                  {!isLoading && kpi.key !== 'empresas_total' && serie.length > 1 && (
                    <Box flexShrink={0} pb={1}>
                      <Sparkline data={serie} color={kpi.accent} />
                    </Box>
                  )}
                </Flex>

                {!isLoading && data && kpi.key === 'empresas_total' && (
                  <Flex direction="column" gap={1.5} mt="auto">
                    <EmpresaLinha
                      label="Ativas"
                      valor={data.empresas_ativas}
                      total={value}
                      cor="#0EA371"
                    />
                    <EmpresaLinha
                      label="Inativas"
                      valor={data.empresas_inativas}
                      total={value}
                      cor="#E0524D"
                    />
                  </Flex>
                )}

                {!isLoading && data && kpi.key !== 'empresas_total' && (
                  <Flex align="center" gap={2} mt="auto" wrap="wrap">
                    {delta != null && delta !== 0 && (
                      <Flex
                        align="center"
                        gap={0.5}
                        px={1.5}
                        py={0.5}
                        borderRadius="md"
                        bg={`${deltaColor}14`}
                        color={deltaColor}
                      >
                        {delta < 0 ? (
                          <CaretDown size={10} weight="bold" />
                        ) : (
                          <CaretUp size={10} weight="bold" />
                        )}
                        <Text fontSize="10px" fontWeight="700">
                          {formatNumber(Math.abs(delta))}
                        </Text>
                      </Flex>
                    )}
                    {kpi.key === 'conciliados' && data.lancamentos_total > 0 && (
                      <Text fontSize="11px" color={kpi.accent} fontWeight="600">
                        {data.percent_conciliado.toFixed(1)}% do total
                      </Text>
                    )}
                    {kpi.sub && kpi.sub(data) && (
                      <Text fontSize="11px" color="#94A3B8" fontWeight="500">
                        {kpi.sub(data)}
                      </Text>
                    )}
                  </Flex>
                )}
              </Flex>
            </GlassCard>
          </motion.div>
        );
      })}
    </SimpleGrid>
  );
};
